import { DeleteButton, EditButton, List, ShowButton, getDefaultSortOrder, useTable } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import dayjs from "dayjs";
import { useState } from "react";
import { Form, Image, Input, Select, Space, Table, Tag, Typography } from "antd";

import { BulkDelete } from "../../components/BulkDelete";
import { CONTENT_STATUSES, formatCoordinates, formatYear, statusMeta } from "../../lib/constants";

interface Photo {
  id: string;
  imageUrl?: string;
  location?: { latitude?: number; longitude?: number };
  year?: number;
  yearTo?: number;
  description?: string;
  sourceUrl?: string;
  status?: string;
  createdBy?: { username?: string };
  createdAt?: string;
}

interface PhotoFilters {
  search?: string;
  status?: string;
}

export function PhotoList() {
  // Фильтры описываются формой Refine: она связывает поля с фильтрами запроса,
  // а поиск уходит на сервер, а не режет уже загруженную страницу.
  const { tableProps, searchFormProps, sorters, tableQuery } = useTable<Photo, HttpError, PhotoFilters>({
    resource: "photos",
    onSearch: (values) => [
      { field: "search", operator: "contains", value: values.search },
      { field: "status", operator: "eq", value: values.status },
    ],
    // Сервер умеет сортировать только по дате добавления — её и даём
    // переключать. По умолчанию новые сверху.
    sorters: { initial: [{ field: "createdAt", order: "desc" }] },
  });

  // Выбор переживает переход по страницам: можно отметить записи на
  // нескольких страницах и удалить их одним действием.
  const [selected, setSelected] = useState<string[]>([]);
  // Подписи запоминаются в момент выбора: строки с других страниц в таблице
  // уже не видны, а в отчёте об ошибках их надо назвать.
  const [labels, setLabels] = useState<Record<string, string>>({});
  const photoLabel = (id: string) => labels[id] ?? "Снимок без описания";
  const select = (keys: React.Key[], rows: Photo[]) => {
    setSelected(keys as string[]);
    setLabels((prev) => {
      const next = { ...prev };
      for (const row of rows) if (row) next[row.id] = row.description || "Снимок без описания";
      return next;
    });
  };

  return (
    <List title="Фотографии">
      <Form
        {...searchFormProps}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 8 }}
        onValuesChange={() => searchFormProps.form?.submit()}
      >
        <Form.Item name="search">
          <Input.Search placeholder="Поиск по описанию или источнику" allowClear style={{ width: 320 }} />
        </Form.Item>

        <Form.Item name="status">
          <Select
            placeholder="Статус"
            allowClear
            style={{ width: 200 }}
            options={CONTENT_STATUSES.map((item) => ({ value: item.value, label: item.label }))}
          />
        </Form.Item>
      </Form>

      <BulkDelete
        path="/api/v1/admin/photos"
        ids={selected}
        noun={{ one: "снимок", few: "снимка", many: "снимков" }}
        describe={(id) => photoLabel(id)}
        onClear={() => setSelected([])}
        onDone={() => {
          setSelected([]);
          void tableQuery.refetch();
        }}
      />

      <Table
        {...tableProps}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: select,
          preserveSelectedRowKeys: true,
        }}
        rowKey="id"
        scroll={{ x: 900 }}
      >
        <Table.Column<Photo>
          title="Снимок"
          dataIndex="imageUrl"
          width={110}
          render={(url?: string) =>
            url ? (
              <Image src={url} width={80} height={60} style={{ objectFit: "cover", borderRadius: 4 }} />
            ) : (
              <Typography.Text type="secondary">нет файла</Typography.Text>
            )
          }
        />

        <Table.Column<Photo>
          title="Описание"
          dataIndex="description"
          render={(description?: string, record?: Photo) => (
            <Space direction="vertical" size={0}>
              <Typography.Text>{description || "Без описания"}</Typography.Text>
              {record?.sourceUrl && (
                <Typography.Link href={record.sourceUrl} target="_blank" style={{ fontSize: 12 }}>
                  источник
                </Typography.Link>
              )}
            </Space>
          )}
        />

        <Table.Column<Photo>
          title="Год"
          dataIndex="year"
          width={130}
          // Период показывается целиком: по одному его началу не отличить
          // точную датировку от приблизительной, а это разные раунды.
          render={(year: number | undefined, record: Photo) =>
            record.yearTo && record.yearTo > (year ?? 0)
              ? `${formatYear(year)} — ${formatYear(record.yearTo)}`
              : formatYear(year)
          }
        />

        <Table.Column<Photo>
          title="Координаты"
          dataIndex="location"
          width={170}
          render={(location?: Photo["location"]) =>
            formatCoordinates(location?.latitude, location?.longitude)
          }
        />

        <Table.Column<Photo>
          title="Добавлено"
          dataIndex="createdAt"
          width={160}
          sorter
          sortDirections={["descend", "ascend", "descend"]}
          defaultSortOrder={getDefaultSortOrder("createdAt", sorters)}
          render={(value?: string) => (value ? dayjs(value).format("D MMM YYYY, HH:mm") : "—")}
        />

        <Table.Column<Photo>
          title="Статус"
          dataIndex="status"
          width={140}
          render={(status?: string) => {
            const meta = statusMeta(status);
            return meta ? <Tag color={meta.color}>{meta.label}</Tag> : "—";
          }}
        />

        <Table.Column<Photo>
          title="Действия"
          width={140}
          fixed="right"
          render={(_, record) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} />
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton
                hideText
                size="small"
                recordItemId={record.id}
                confirmTitle="Удалить снимок?"
                confirmOkText="Удалить"
                confirmCancelText="Отмена"
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
